import { mapEventTimesToUpdateInput } from '@griply/services';
import {
  ActionEvents,
  CalendarEventOccurrenceTarget,
  ExternalConnection,
  ExternalConnectionsProviders,
} from '@griply/types';
import { getConnectionProvider } from '@griply/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { queryKey as externalEventsQueryKey } from 'shared/hooks/queries/use-external-calendar-events-query';
import { useResolveExternalEventRecurrence } from 'shared/hooks/queries/use-resolve-external-event-recurrence';
import { useUpdateExternalCalendarEventMutation } from 'shared/hooks/queries/use-update-external-calendar-event-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { ExternalEventDragRequest } from 'shared/lib/full-calendar';

export type PendingDrag = {
  request: ExternalEventDragRequest;
  revert: () => void;
};

export type BlockedMove = {
  provider: ExternalConnectionsProviders;
  url: string | undefined;
};

export type UseEventDragFlowOptions = {
  connections: ExternalConnection[];
  editAllowed: boolean;
  // shared with the popup's submit path, so the provider owns it and passes it down
  update: ReturnType<typeof useUpdateExternalCalendarEventMutation>;
};

export type EventDragFlow = {
  pendingDrag: PendingDrag | null;
  blockedMove: BlockedMove | null;
  onDragEvent: (
    request: ExternalEventDragRequest,
    revert: () => void,
  ) => Promise<void>;
  onSelectDragOccurrences: (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => void;
  onCancelDragOccurrences: () => void;
  closeBlockedMove: () => void;
};

export const useEventDragFlow = ({
  connections,
  editAllowed,
  update,
}: UseEventDragFlowOptions): EventDragFlow => {
  const [pendingDrag, setPendingDrag] = useState<PendingDrag | null>(null);
  const [blockedMove, setBlockedMove] = useState<BlockedMove | null>(null);

  const track = useTrackEvents();
  const queryClient = useQueryClient();
  const openPremiumDialog = useOpenPremiumDialog();
  const resolveIsRecurring = useResolveExternalEventRecurrence();

  const writeDrag = useCallback(
    (
      request: ExternalEventDragRequest,
      occurrenceTarget: CalendarEventOccurrenceTarget,
    ) => {
      update.mutate({
        payload: mapEventTimesToUpdateInput(request.eventRef, {
          allDay: request.allDay,
          start: request.start,
          end: request.end,
          occurrenceTarget,
          // the grid paints in the browser's zone, so that is the zone its boundaries are in
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
        optimistic: request.optimistic,
      });
      track(ActionEvents.CalendarEventUpdate, {
        provider: getConnectionProvider(
          connections,
          request.eventRef.connectionId,
        ),
        all_day: request.allDay,
        has_guests: false,
        has_video_call: false,
        is_recurring: request.isRecurring,
        edit_scope: occurrenceTarget,
      });
    },
    [connections, track, update],
  );

  const onDragEvent = useCallback(
    async (request: ExternalEventDragRequest, revert: () => void) => {
      // Refused before anything is awaited, so the event snaps back under the pointer.
      if (!editAllowed) {
        revert();
        openPremiumDialog();
        return;
      }

      // Only an explicit `false` refuses: an older backend leaves the flag absent and the
      // write fails at the provider as before.
      if (request.isOrganizer === false) {
        revert();
        setBlockedMove({
          provider: getConnectionProvider(
            connections,
            request.eventRef.connectionId,
          ),
          url: request.url,
        });
        return;
      }

      // A false flag only means "not known to repeat" — expanded occurrences and older backends both do.
      const isRecurring =
        request.isRecurring || (await resolveIsRecurring(request.eventRef));
      if (isRecurring) {
        setPendingDrag({ request, revert });
        return;
      }
      writeDrag(request, CalendarEventOccurrenceTarget.Single);
    },
    [
      connections,
      editAllowed,
      openPremiumDialog,
      resolveIsRecurring,
      writeDrag,
    ],
  );

  const closeBlockedMove = useCallback(() => setBlockedMove(null), []);

  const onSelectDragOccurrences = useCallback(
    (occurrenceTarget: CalendarEventOccurrenceTarget) => {
      if (pendingDrag) {
        writeDrag(pendingDrag.request, occurrenceTarget);
      }
      setPendingDrag(null);
    },
    [pendingDrag, writeDrag],
  );

  // Nothing was written, so undo FullCalendar's move; the refetch backstops a stale EventApi.
  const onCancelDragOccurrences = useCallback(() => {
    try {
      pendingDrag?.revert();
    } catch {
      // stale FullCalendar EventImpl — the refetch below restores the server position
    }
    setPendingDrag(null);
    queryClient.invalidateQueries({ queryKey: [externalEventsQueryKey] });
  }, [pendingDrag, queryClient]);

  return {
    pendingDrag,
    blockedMove,
    onDragEvent,
    onSelectDragOccurrences,
    onCancelDragOccurrences,
    closeBlockedMove,
  };
};
